// frontend/src/settings/ShiftDeadlineSettings.js
// シフト希望の受付サイクル管理 (詳細スケジュール + legacy 月次締切フォールバック)。
//
// 設計:
//   - shift_submission_cycles テーブルで任意の受付サイクル (対象期間 + 締切) を複数登録。
//   - cycle が 1 件もない場合は holiday_policies.submission_deadline_day の legacy 計算を使う。
//   - MobileShiftForm は cycle を優先し、無ければ legacy にフォールバック。

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../supabase';
import { useOrganization } from '../contexts/OrganizationContext';
import Header from '../components/ui/Header';
import { Plus, Trash2, Save, Calendar, Clock, Edit3, X } from 'lucide-react';

const formatDate = (d) => (d ? d : '');
const formatDateTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const yy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${yy}/${mm}/${dd} ${hh}:${mi}`;
};
const toLocalDatetimeInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const fromLocalDatetimeInput = (val) => {
  if (!val) return null;
  return new Date(val).toISOString();
};

function ShiftDeadlineSettings() {
  const { currentOrganization } = useOrganization();
  const [deadlineDay, setDeadlineDay] = useState(10);
  const [cycles, setCycles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [savedMessage, setSavedMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [editingCycle, setEditingCycle] = useState(null); // null | 'new' | row

  const fetchSettings = useCallback(async () => {
    if (!currentOrganization?.id) return;
    try {
      setLoading(true);
      const { data: policy } = await supabase
        .from('holiday_policies')
        .select('submission_deadline_day')
        .eq('organization_id', currentOrganization.id)
        .maybeSingle();
      if (policy?.submission_deadline_day) setDeadlineDay(policy.submission_deadline_day);

      const { data: cyclesData, error: cyclesError } = await supabase
        .from('shift_submission_cycles')
        .select('*')
        .eq('organization_id', currentOrganization.id)
        .order('submission_deadline', { ascending: true });
      if (cyclesError) throw cyclesError;
      setCycles(cyclesData || []);
    } catch (e) {
      console.error('設定読み込みエラー:', e);
    } finally {
      setLoading(false);
    }
  }, [currentOrganization?.id]);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const flashMessage = (msg) => {
    setSavedMessage(msg);
    setTimeout(() => setSavedMessage(''), 3000);
  };

  const saveLegacy = async () => {
    const day = parseInt(deadlineDay);
    if (!day || day < 1 || day > 31) {
      alert('1〜31 の数字を入力してください');
      return;
    }
    setSaving(true);
    try {
      const { error } = await supabase
        .from('holiday_policies')
        .upsert({
          organization_id: currentOrganization.id,
          submission_deadline_day: day,
          updated_at: new Date().toISOString(),
        }, { onConflict: 'organization_id' });
      if (error) throw error;
      flashMessage('✓ 既定の締切日を保存しました');
    } catch (e) {
      alert('保存に失敗しました: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  const saveCycle = async (cycle) => {
    if (!cycle.target_period_start || !cycle.target_period_end || !cycle.submission_deadline) {
      alert('対象期間 (開始/終了) と締切は必須です');
      return;
    }
    if (new Date(cycle.target_period_end) < new Date(cycle.target_period_start)) {
      alert('対象期間の終了日は開始日以降にしてください');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        organization_id: currentOrganization.id,
        name: cycle.name || null,
        target_period_start: cycle.target_period_start,
        target_period_end: cycle.target_period_end,
        submission_deadline: cycle.submission_deadline,
      };
      if (cycle.id) {
        const { error } = await supabase
          .from('shift_submission_cycles')
          .update(payload)
          .eq('id', cycle.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('shift_submission_cycles')
          .insert(payload);
        if (error) throw error;
      }
      setEditingCycle(null);
      await fetchSettings();
      flashMessage('✓ サイクルを保存しました');
    } catch (e) {
      alert('保存に失敗しました: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  const deleteCycle = async (id) => {
    if (!window.confirm('このサイクルを削除しますか？')) return;
    try {
      const { error } = await supabase
        .from('shift_submission_cycles')
        .delete()
        .eq('id', id);
      if (error) throw error;
      await fetchSettings();
      flashMessage('✓ サイクルを削除しました');
    } catch (e) {
      alert('削除に失敗しました: ' + e.message);
    }
  };

  const openNewCycle = () => {
    const today = new Date();
    const nextMonthFirst = new Date(today.getFullYear(), today.getMonth() + 1, 1);
    const nextMonthLast = new Date(today.getFullYear(), today.getMonth() + 2, 0);
    const deadline = new Date(today.getFullYear(), today.getMonth(), deadlineDay, 23, 59);
    setEditingCycle({
      id: null,
      name: '',
      target_period_start: formatDate(nextMonthFirst.toISOString().slice(0, 10)),
      target_period_end: formatDate(nextMonthLast.toISOString().slice(0, 10)),
      submission_deadline: deadline.toISOString(),
    });
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <Header />
        <div style={{ textAlign: 'center', padding: '2rem' }}>読み込み中...</div>
      </div>
    );
  }

  const today = new Date();
  const exampleDeadline = `${today.getMonth() + 1}月${deadlineDay}日`;

  return (
    <div style={styles.container}>
      <Header />
      <Link to="/settings" style={styles.backLink}>← 設定メニューへ戻る</Link>

      {savedMessage && (
        <div style={styles.toast}>{savedMessage}</div>
      )}

      {/* サイクル一覧 */}
      <div style={styles.card}>
        <div style={styles.header}>
          <h2 style={{ margin: 0 }}>シフト希望の受付サイクル</h2>
          <button onClick={openNewCycle} style={styles.btnPrimary}>
            <Plus size={14} />新しいサイクルを追加
          </button>
        </div>

        <div style={styles.infoBox}>
          <p style={{ margin: 0, fontSize: '0.9rem', color: '#856404' }}>
            💡 対象期間と締切のセットを「サイクル」として自由に登録できます。<br />
            例: 2月の締切で 3月分 + 4月分 を一括受付 → 2 サイクル作って、締切日時を同じにする。
          </p>
        </div>

        {cycles.length === 0 ? (
          <div style={styles.empty}>
            まだサイクルは登録されていません。
            下記の「既定の月次締切」が使用されます。
          </div>
        ) : (
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>名前</th>
                  <th style={styles.th}>対象期間</th>
                  <th style={styles.th}>締切</th>
                  <th style={styles.th}>状態</th>
                  <th style={styles.thRight}>操作</th>
                </tr>
              </thead>
              <tbody>
                {cycles.map((c) => {
                  const isLocked = new Date(c.submission_deadline).getTime() < Date.now();
                  return (
                    <tr key={c.id} style={isLocked ? styles.rowLocked : styles.rowOpen}>
                      <td style={styles.td}>{c.name || '-'}</td>
                      <td style={styles.td}>
                        <Calendar size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                        {c.target_period_start} 〜 {c.target_period_end}
                      </td>
                      <td style={styles.td}>
                        <Clock size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                        {formatDateTime(c.submission_deadline)}
                      </td>
                      <td style={styles.td}>
                        {isLocked
                          ? <span style={styles.badgeLocked}>締切済</span>
                          : <span style={styles.badgeOpen}>受付中</span>}
                      </td>
                      <td style={styles.tdRight}>
                        <button onClick={() => setEditingCycle(c)} style={styles.btnGhost}>
                          <Edit3 size={12} />編集
                        </button>
                        <button onClick={() => deleteCycle(c.id)} style={styles.btnDanger}>
                          <Trash2 size={12} />削除
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* legacy 月次締切 */}
      <div style={styles.card}>
        <div style={styles.header}>
          <h2 style={{ margin: 0 }}>既定の月次締切 (サイクル未登録時のフォールバック)</h2>
        </div>
        <div style={styles.infoBox}>
          <p style={{ margin: 0, fontSize: '0.9rem', color: '#856404' }}>
            ⚠️ サイクルが 1 件以上登録されている場合、この設定は無視されます。
          </p>
        </div>
        <div>
          <label style={styles.label}>締め切り日 (毎月)</label>
          <div style={styles.inputRow}>
            <input
              type="number"
              min="1"
              max="31"
              value={deadlineDay}
              onChange={e => setDeadlineDay(e.target.value)}
              style={styles.input}
            />
            <span style={styles.unit}>日</span>
          </div>
          <p style={styles.helpText}>毎月この日の 23:59 まで提出・変更が可能になります。</p>

          <div style={styles.preview}>
            <div style={styles.previewLabel}>📅 今月の締め切り (例)</div>
            <div style={styles.previewValue}>{exampleDeadline}（23:59まで）</div>
          </div>
        </div>
        <div style={styles.buttonContainer}>
          <button onClick={saveLegacy} disabled={saving} style={styles.btnPrimary}>
            <Save size={14} />{saving ? '保存中...' : '既定の締切を保存'}
          </button>
        </div>
      </div>

      {/* サイクル編集モーダル */}
      {editingCycle && (
        <div style={styles.modalBackdrop} onClick={() => setEditingCycle(null)}>
          <div style={styles.modalCard} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={{ margin: 0 }}>{editingCycle.id ? 'サイクルを編集' : '新しいサイクルを追加'}</h3>
              <button onClick={() => setEditingCycle(null)} style={styles.iconBtn}><X size={20} /></button>
            </div>
            <div style={styles.modalBody}>
              <label style={styles.label}>名前 (任意)</label>
              <input
                type="text"
                value={editingCycle.name || ''}
                onChange={(e) => setEditingCycle({ ...editingCycle, name: e.target.value })}
                placeholder="例: 2026年3月期 / 春期 等"
                style={styles.input2}
              />

              <label style={styles.label}>対象期間 開始日 *</label>
              <input
                type="date"
                value={editingCycle.target_period_start}
                onChange={(e) => setEditingCycle({ ...editingCycle, target_period_start: e.target.value })}
                style={styles.input2}
              />

              <label style={styles.label}>対象期間 終了日 *</label>
              <input
                type="date"
                value={editingCycle.target_period_end}
                onChange={(e) => setEditingCycle({ ...editingCycle, target_period_end: e.target.value })}
                style={styles.input2}
              />

              <label style={styles.label}>希望提出 締切日時 *</label>
              <input
                type="datetime-local"
                value={toLocalDatetimeInput(editingCycle.submission_deadline)}
                onChange={(e) => setEditingCycle({
                  ...editingCycle,
                  submission_deadline: fromLocalDatetimeInput(e.target.value),
                })}
                style={styles.input2}
              />
              <p style={styles.helpText}>この日時を過ぎたらその期間はロックされます。</p>
            </div>
            <div style={styles.modalFooter}>
              <button onClick={() => setEditingCycle(null)} style={styles.btnGhost}>キャンセル</button>
              <button onClick={() => saveCycle(editingCycle)} disabled={saving} style={styles.btnPrimary}>
                <Save size={14} />{saving ? '保存中...' : '保存'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: { padding: '2rem', maxWidth: 820, margin: '0 auto', fontFamily: 'sans-serif' },
  backLink: { display: 'inline-block', marginBottom: '1rem', color: '#007bff', textDecoration: 'none' },
  card: { backgroundColor: '#fff', borderRadius: 8, boxShadow: '0 2px 10px rgba(0,0,0,0.08)', padding: '1.5rem', marginBottom: '1.5rem', border: '1px solid #e0e0e0' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #eee', paddingBottom: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' },
  infoBox: { backgroundColor: '#fff3cd', padding: '0.75rem 1rem', borderRadius: 6, marginBottom: '1rem', border: '1px solid #ffc107' },
  label: { display: 'block', marginBottom: '0.4rem', fontWeight: 500, color: '#333', fontSize: '0.9rem', marginTop: '0.75rem' },
  inputRow: { display: 'flex', alignItems: 'center', gap: '0.5rem' },
  input: { width: 80, padding: '0.6rem', borderRadius: 4, border: '1px solid #ccc', fontSize: '1rem', textAlign: 'center' },
  input2: { width: '100%', padding: '0.6rem', borderRadius: 6, border: '1px solid #ccc', fontSize: '0.95rem', boxSizing: 'border-box' },
  unit: { fontSize: '1rem', color: '#333' },
  helpText: { fontSize: '0.8rem', color: '#666', marginTop: '0.4rem' },
  preview: { marginTop: '1rem', padding: '0.75rem', backgroundColor: '#f0f9ff', borderRadius: 6, border: '1px solid #bae6fd' },
  previewLabel: { fontSize: '0.8rem', color: '#0369a1', fontWeight: 600 },
  previewValue: { fontSize: '0.95rem', color: '#0c4a6e' },
  buttonContainer: { marginTop: '1.5rem', display: 'flex', justifyContent: 'flex-end' },
  btnPrimary: { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '0.5rem 1rem', backgroundColor: '#007bff', color: '#fff', border: 'none', borderRadius: 6, fontSize: '0.9rem', cursor: 'pointer', fontWeight: 600 },
  btnGhost: { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '0.3rem 0.75rem', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 4, fontSize: '0.8rem', cursor: 'pointer', marginRight: 4 },
  btnDanger: { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '0.3rem 0.75rem', background: '#fff', color: '#dc2626', border: '1px solid #fca5a5', borderRadius: 4, fontSize: '0.8rem', cursor: 'pointer' },
  empty: { padding: '1.5rem', textAlign: 'center', color: '#6b7280', fontSize: '0.9rem', background: '#f9fafb', borderRadius: 6 },
  tableWrap: { overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' },
  th: { textAlign: 'left', padding: '0.6rem', background: '#f8fafc', borderBottom: '1px solid #e5e7eb', fontWeight: 600, color: '#475569', fontSize: '0.8rem' },
  thRight: { textAlign: 'right', padding: '0.6rem', background: '#f8fafc', borderBottom: '1px solid #e5e7eb' },
  td: { padding: '0.75rem 0.6rem', borderBottom: '1px solid #f3f4f6', color: '#111827' },
  tdRight: { padding: '0.75rem 0.6rem', borderBottom: '1px solid #f3f4f6', textAlign: 'right' },
  rowOpen: { background: '#fff' },
  rowLocked: { background: '#f9fafb', color: '#9ca3af' },
  badgeOpen: { background: '#dcfce7', color: '#166534', padding: '2px 8px', borderRadius: 999, fontSize: '0.7rem', fontWeight: 700 },
  badgeLocked: { background: '#fee2e2', color: '#991b1b', padding: '2px 8px', borderRadius: 999, fontSize: '0.7rem', fontWeight: 700 },
  toast: { position: 'fixed', top: 20, right: 20, background: '#16a34a', color: '#fff', padding: '10px 16px', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 10000, fontSize: '0.9rem' },
  modalBackdrop: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10001, padding: '1rem' },
  modalCard: { background: '#fff', borderRadius: 12, width: '100%', maxWidth: 460, boxShadow: '0 12px 40px rgba(0,0,0,0.2)' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1.25rem', borderBottom: '1px solid #e5e7eb' },
  modalBody: { padding: '1rem 1.25rem' },
  modalFooter: { padding: '0.75rem 1.25rem', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end', gap: 8 },
  iconBtn: { background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, color: '#6b7280' },
};

export default ShiftDeadlineSettings;
